import { cn } from '@/lib/utils';
import React from 'react';

import { FluidHoverHighlight } from '@/components/fluid-hover-highlight';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';

import {
    type UseFluidHoverReturn,
    useFluidHover,
    useRegisterFluidHoverItem
} from '@/hooks/use-fluid-hover';

export interface VerticalTabItem {
    value: string;
    label: string;
}

const VERTICAL_TAB_TRIGGER_CLASSNAME =
    'relative z-10 w-auto md:w-full shrink-0 justify-start whitespace-nowrap px-4 py-2 rounded-md font-normal text-base text-foreground data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:shadow-none';

interface VerticalTabProps {
    tab: VerticalTabItem;
    index: number;
    registerItem: UseFluidHoverReturn['registerItem'];
}

const VerticalTab: React.FC<VerticalTabProps> = ({
    tab,
    index,
    registerItem
}) => {
    const ref = React.useRef<HTMLButtonElement | null>(null);
    useRegisterFluidHoverItem(registerItem, index, ref);

    return (
        <TabsTrigger
            ref={ref}
            value={tab.value}
            className={VERTICAL_TAB_TRIGGER_CLASSNAME}
        >
            {tab.label}
        </TabsTrigger>
    );
};

interface VerticalTabsListProps {
    tabs: VerticalTabItem[];
    className?: string;
}

export const VerticalTabsList: React.FC<VerticalTabsListProps> = ({
    tabs,
    className
}) => {
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const hover = useFluidHover(listRef, { gapClick: false });

    return (
        <TabsList
            ref={listRef}
            indicatorClassName="bg-muted rounded-md"
            onMouseEnter={hover.handlers.onMouseEnter}
            onMouseMove={hover.handlers.onMouseMove}
            onMouseLeave={hover.handlers.onMouseLeave}
            className={cn(
                'flex flex-row md:flex-col justify-start items-stretch gap-1 bg-transparent p-0 w-full md:w-56 h-auto overflow-x-auto md:overflow-x-visible scrollbar-hidden',
                className
            )}
        >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <FluidHoverHighlight hover={hover} className="rounded-md" />
            </div>
            {tabs.map((tab, index) => (
                <VerticalTab
                    key={tab.value}
                    tab={tab}
                    index={index}
                    registerItem={hover.registerItem}
                />
            ))}
        </TabsList>
    );
};
